import React, { useCallback, useEffect, useRef, useState } from "react";

interface Anchor {
  x: number;
  y: number;
}

interface RadialKnobProps {
  radius: number;
  anchors: Anchor[];
  onWeightsChange: (weights: number[]) => void;
}

export const RadialKnob: React.FC<RadialKnobProps> = ({
  radius,
  anchors,
  onWeightsChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [knobPos, setKnobPos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [isDragging, setIsDragging] = useState(false);

  const computeWeights = useCallback(
    (knobX: number, knobY: number): number[] => {
      const epsilon = 1e-6;
      const distances = anchors.map((anchor) =>
        Math.sqrt((knobX - anchor.x) ** 2 + (knobY - anchor.y) ** 2)
      );
      const invDistances = distances.map((d) => 1 / (d + epsilon));
      const sum = invDistances.reduce((a, b) => a + b, 0);
      return invDistances.map((inv) => inv / sum);
    },
    [anchors]
  );

  const updateKnobPosition = useCallback(
    (e: PointerEvent) => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      let x = e.clientX - rect.left - centerX;
      let y = e.clientY - rect.top - centerY;

      const distance = Math.sqrt(x * x + y * y);
      if (distance > radius) {
        const scale = radius / distance;
        x *= scale;
        y *= scale;
      }

      setKnobPos({ x, y });
    },
    [radius]
  );

  useEffect(() => {
    const weights = computeWeights(knobPos.x, knobPos.y);
    onWeightsChange(weights);
  }, [knobPos, computeWeights, onWeightsChange]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    updateKnobPosition(e.nativeEvent);
  };

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, [setIsDragging]);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDragging || !containerRef.current) return;

      updateKnobPosition(e);
    },
    [isDragging, updateKnobPosition]
  );

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    } else {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    }
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging, handlePointerMove, handlePointerUp]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: `${radius * 2}px`,
        height: `${radius * 2}px`,
        background: "#000",
        borderRadius: "50%",
        border: "2px solid #000",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        touchAction: "none",
      }}
      onPointerDown={handlePointerDown}
    >
      <div
        style={{
          position: "absolute",
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          background: "#fff",
          border: "1px solid #000",
          transform: `translate(${knobPos.x}px, ${knobPos.y}px)`,
        }}
      />
    </div>
  );
};
